import { useTranslation } from "react-i18next";

import OpenArrow from "@/assets/ux/ArrowOpenDoc.svg?react";
import { TableRow } from "@/components/Layout/Table/TableRow/TableRow";
import { DrEvn } from "@/services/model";
import { FCC } from "@/types";
import { formatYearMonth } from "@/utils/dateHelpers";
import { getCurrency } from "@/utils/translationHelpers";

import { ItemizedBillCheckbox } from "./ItemizedBillCheckbox";

export interface iItemizedBillOverviewRowProps {
  itemizedBill: DrEvn;
  downloadItemizedBill: ({
    serviceId,
    period,
  }: {
    serviceId: number;
    period: string;
  }) => void;
  variant?: "default" | "compact";
  isSelectMultiple?: boolean;
}

const DataRow: FCC = ({ children }) => (
  <td className="max-w-52 truncate text-left">{children}</td>
);

export const ItemizedBillOverviewRow = ({
  itemizedBill: { serviceId, contract, periode: period, amount, billNumber },
  downloadItemizedBill,
  variant = "default",
  isSelectMultiple,
}: iItemizedBillOverviewRowProps) => {
  const { i18n } = useTranslation();

  if (!serviceId || !period) {
    return null;
  }

  if (variant === "compact") {
    return (
      <div className="flex gap-4 border-b border-blue-200 py-4">
        <div className="flex-1">
          <div className="flex items-start">
            <span className="flex-1 break-words text-left text-base font-semibold">
              {contract}
            </span>
          </div>

          <div className="mt-1 flex gap-4 text-left text-base">
            <span>
              {formatYearMonth(period, i18n.language)} {billNumber}
            </span>
            <span className="shrink-0 text-base font-semibold text-primary-100">
              {getCurrency(amount || 0, "CHF")}
            </span>
          </div>
        </div>

        {isSelectMultiple ? (
          <div className="flex shrink-0 items-center">
            <ItemizedBillCheckbox serviceId={serviceId} period={period} />
          </div>
        ) : (
          <button
            data-testid="mobile-bill-list-row-download"
            className="shrink-0 text-highlight-100 transition-opacity hover:opacity-80"
            onClick={() => downloadItemizedBill({ serviceId, period })}
          >
            <OpenArrow />
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <TableRow>
        <td className="text-base leading-normal">
          <ItemizedBillCheckbox serviceId={serviceId} period={period} />
        </td>
        <DataRow>{contract}</DataRow>
        <DataRow>{formatYearMonth(period, i18n.language)}</DataRow>
        <DataRow>{getCurrency(amount || 0, "CHF")}</DataRow>
        <DataRow>{billNumber}</DataRow>
      </TableRow>
    </>
  );
};
